import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, Mail } from "lucide-react";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [status, setStatus] = useState("verifying"); // verifying | success | error
  const [message, setMessage] = useState("");
  const [resending, setResending] = useState(false);

  useEffect(() => {
    const token = params.get("token");
    if (!token) { setStatus("error"); setMessage("This link is missing its token."); return; }
    api.post("/auth/verify-email", { token })
      .then(({ data }) => { setStatus("success"); setMessage(data.message); refreshUser?.(); })
      .catch((err) => { setStatus("error"); setMessage(err.response?.data?.detail || "We couldn't verify this link."); });
    // eslint-disable-next-line
  }, []);

  const resend = async () => {
    if (!user) { navigate("/auth"); return; }
    setResending(true);
    try {
      await api.post("/auth/resend-verification", { origin: window.location.origin });
      toast.success("New verification email sent — check your inbox.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not resend right now.");
    } finally { setResending(false); }
  };

  return (
    <div className="min-h-screen bg-background text-white flex items-center justify-center px-6" data-testid="verify-email-page">
      <div className="max-w-md w-full rounded-2xl border border-white/10 bg-zinc-950/60 p-8 text-center">
        <div className="text-xl font-semibold tracking-tight mb-6">VIRAL<span className="text-[#00E5FF]">3D</span></div>
        {status === "verifying" && (
          <div data-testid="verify-loading"><Loader2 className="w-10 h-10 mx-auto animate-spin text-[#00E5FF] mb-4" /><p className="text-white/60">Verifying your email…</p></div>
        )}
        {status === "success" && (
          <div data-testid="verify-success">
            <CheckCircle2 className="w-12 h-12 mx-auto text-[#39FF14] mb-4" />
            <h1 className="text-2xl font-semibold mb-2">Email verified</h1>
            <p className="text-white/60 mb-6">{message}</p>
            <Button onClick={() => navigate("/dashboard")} className="rounded-full bg-[#00E5FF] text-black hover:bg-[#00E5FF]/90" data-testid="verify-go-dashboard">Go to dashboard</Button>
          </div>
        )}
        {status === "error" && (
          <div data-testid="verify-error">
            <XCircle className="w-12 h-12 mx-auto text-[#FF007F] mb-4" />
            <h1 className="text-2xl font-semibold mb-2">Verification failed</h1>
            <p className="text-white/60 mb-6">{message}</p>
            <Button onClick={resend} disabled={resending} className="rounded-full bg-white/10 hover:bg-white/20 text-white" data-testid="verify-resend-btn">
              {resending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}Resend verification email
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
